import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useConnectionStatus } from '@/hooks'
import { cn } from '@/lib/utils'
import type { Connector } from '@/types'
import { CONNECTOR_LABELS, REPOSITORY_CONNECTOR_TYPES } from './connectorConstants'
import { CONNECTOR_ICONS } from './connectorIcons'
import { RepositoryPicker } from './RepositoryPicker'
import { WikiPicker } from './WikiPicker'

interface ConnectionRowProps {
  projectId: string
  connection: Connector
  canManage: boolean
  onDisconnect: (connection: Connector) => void
  onReconnect: (connection: Connector) => void
}

export function ConnectionRow({ projectId, connection, canManage, onDisconnect, onReconnect }: Readonly<ConnectionRowProps>) {
  const [isExpanded, setIsExpanded] = useState(false)
  const Icon = CONNECTOR_ICONS[connection.type]
  const hasRepositories = REPOSITORY_CONNECTOR_TYPES.includes(connection.type)
  const hasWikis = connection.type === 'AzureDevOps'
  const isExpandable = hasRepositories || hasWikis
  const displayName = connection.connectionName || CONNECTOR_LABELS[connection.type]
  const detailsId = `connection-row-${connection.id}-details`
  const { data: status } = useConnectionStatus(projectId, connection.id, connection.isEnabled)
  const isExpired = status?.isExpired === true

  const identity = (
    <>
      {isExpandable && (
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-foreground transition-transform duration-200', isExpanded ? 'rotate-0' : '-rotate-90')}
        />
      )}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Icon className="h-5 w-5" />
      </div>
      <span className="truncate text-sm font-semibold">{displayName}</span>
      {!connection.isEnabled && (
        <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">Disabled</span>
      )}
      {isExpired && (
        <span
          className="rounded-full border border-danger-foreground/40 bg-danger px-2 py-0.5 text-xs font-medium text-danger-foreground"
          data-testid={`connection-row-${connection.id}-expired`}
        >
          Token expired
        </span>
      )}
    </>
  )

  return (
    <li className="rounded-xl border" data-testid={`connection-row-${connection.id}`}>
      <div className="flex items-center justify-between gap-3 p-4">
        {isExpandable ? (
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
            aria-expanded={isExpanded}
            aria-controls={isExpanded ? detailsId : undefined}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            data-testid={`connection-row-${connection.id}-toggle`}
          >
            {identity}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-3">{identity}</div>
        )}

        {canManage && isExpired && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            aria-label={`Reconnect ${displayName}`}
            onClick={() => onReconnect(connection)}
            data-testid={`connection-row-${connection.id}-reconnect`}
          >
            Reconnect
          </Button>
        )}

        {canManage && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="shrink-0"
            aria-label={`Disconnect ${displayName}`}
            onClick={() => onDisconnect(connection)}
            data-testid={`connection-row-${connection.id}-disconnect`}
          >
            Disconnect
          </Button>
        )}
      </div>

      {isExpandable && isExpanded && (
        <div id={detailsId} className="space-y-4 border-t p-4" data-testid={detailsId}>
          {hasRepositories && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Repositories</h4>
              <RepositoryPicker
                projectId={projectId}
                connectorType={connection.type}
                connectorId={connection.id}
                connectionName={displayName}
                onReconnect={canManage ? () => onReconnect(connection) : undefined}
              />
            </div>
          )}
          {hasRepositories && hasWikis && <Separator />}
          {hasWikis && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Wikis</h4>
              <WikiPicker
                projectId={projectId}
                connectorId={connection.id}
                connectionName={displayName}
                onReconnect={canManage ? () => onReconnect(connection) : undefined}
              />
            </div>
          )}
        </div>
      )}
    </li>
  )
}
